class FreeGiftManager {
  constructor(config) {
    this._config = config;
    this._currentPopup = null;
    this._activeCampaignsCache = new Map();
    this._discardedCampaigns = new Set();
    this._init();
  }

  async _init() {
    this._patchFetch();
    await this._setupEventListeners();
  }

  _patchFetch() {
    const originalFetch = window.fetch;
    window.fetch = async (...args) => {
      const response = await originalFetch(...args);
      if (args[0]?.includes('/cart/add') || args[0]?.includes('/cart/change')) {
        queueMicrotask(() => this.checkCart());
      }
      return response;
    };
  }

  async _setupEventListeners() {
    await documentReady();
    document.querySelectorAll('[data-add-to-cart]')
      .forEach(btn => btn.addEventListener('click', () => this.checkCart()));
  }

  async checkCart() {
    try {
      const cart = await this._getCart();
      await this._cleanExpiredGifts(cart);
      this._processCampaigns(cart);
    } catch (error) {
      console.error('Error processing cart:', error);
    }
  }

  async _getCart() {
    const response = await fetch('/cart.js', { cache: 'no-store' });
    const cart = await response.json();
    await this._enrichCartItems(cart);
    return cart;
  }

  async _enrichCartItems(cart) {
    await Promise.allSettled(cart.items.map(async item => {
      if (!item.tags) {
        try {
          const product = await fetch(`/products/${item.handle}.json`, { cache: 'default' });
          item.tags = (await product.json())?.product?.tags ?? [];
        } catch {
          item.tags = [];
        }
      }
    }));
  }

  async _cleanExpiredGifts(cart) {
    const giftIds = this._getAllGiftIds();
    const giftsToRemove = cart.items.filter(item => {
      const activeCampaign = this._config.data.find(campaign =>
        campaign.Product_to_Offers.Product_to_Offer_FreeGift
          .some(gift => gift.freegif === item.id)
      );
      return giftIds.some(g => g.freegif === item.id) &&
        !this._isGiftValid(item.id, cart) &&
        activeCampaign?.Title === item.properties?._free_gift;
    });

    await Promise.allSettled(giftsToRemove.map(item => this._removeFromCart(item.key)));
  }

  _getAllGiftIds() {
    return this._config.data.flatMap(campaign =>
      campaign.Product_to_Offers.Product_to_Offer_FreeGift.map(gift => ({
        freegif: gift.freegif,
        campaignTitle: campaign.Title
      }))
    );
  }

  _isGiftValid(giftId, cart) {
    const campaign = this._config.data.find(c =>
      c.Product_to_Offers.Product_to_Offer_FreeGift.some(g => g.freegif === giftId)
    );
    return campaign ? this._isCampaignApplicable(cart, campaign) : false;
  }

  _processCampaigns(cart) {
    for (const campaign of this._getActiveCampaigns()) {
      if (this._discardedCampaigns.has(campaign.Title)) continue; 

      if (this._isCampaignApplicable(cart, campaign)) {
        this._handleCampaign(campaign, cart);
        break; 
      }
    }
  }

  _getActiveCampaigns() {
    if (!this._activeCampaignsCache.size) {
      const now = Date.now();
      this._activeCampaignsCache = new Map(
        this._config.data
          .filter(c => {
            const start = new Date(c.Date_Start).getTime();
            const end = new Date(c.Date_End).getTime();
            return now >= start && now <= end;
          })
          .sort((a, b) => a.Priority - b.Priority)
          .map(c => [c.Title, c])
      );
    }
    return this._activeCampaignsCache.values();
  }

  _isCampaignApplicable(cart, campaign) {
    const rules = {
      'Quantity of products X in the cart': () => this._checkProducts(cart, campaign),
      'Quantity of Products X with Product Types Y in the cart': () => this._checkProductType(cart, campaign),
      'Quantity of Products X with Tags Y in the cart': () => this._checkTags(cart, campaign),
      'Cart subtotal': () => this._checkPriceRange(cart, campaign),
      'Cart Must Contain Specific Products with N Quantity': () => this._checkMultipleProductsWithQuantities(cart, campaign)
    };
    return rules[campaign.Select_Rules_Option]?.() ?? false;
  }

  _checkProducts(cart, campaign) {
    return cart.items.some(item =>
      campaign.Products.Product_Variant_Id.some(p => p.id === item.id && item.quantity >= p.quantity)
    );
  }

  _checkProductType(cart, campaign) {
    const total = cart.items
      .filter(i => i.product_type === campaign.ProductType && !i.properties?._free_gift)
      .reduce((sum, i) => sum + i.quantity, 0);
    return total >= campaign.Products.product_quantity;
  }

  _checkTags(cart, campaign) {
    const total = cart.items
      .filter(i => !i.properties?._free_gift && i.tags?.split(',').map(t => t.trim()).some(t => campaign.Tags.includes(t)))
      .reduce((sum, i) => sum + i.quantity, 0);
    return total >= campaign.Products.product_quantity;
  }

  _checkPriceRange(cart, campaign) {
    const total = cart.items
      .filter(i => !i.properties?._free_gift)
      .reduce((sum, i) => sum + (i.price * i.quantity), 0) / 100;
    return total >= Number(campaign.priceRange.min) && total <= Number(campaign.priceRange.max);
  }

  _checkMultipleProductsWithQuantities(cart, campaign) {
    return campaign.Products?.Product_Variant_Id?.every(p =>
      cart.items.some(i => i.id === p.id && i.quantity >= p.quantity)
    ) ?? false;
  }

  _handleCampaign(campaign, cart) {
    const gifts = campaign.Product_to_Offers.Product_to_Offer_FreeGift;
    if (!gifts || gifts.length === 0) {
        console.warn(`Campaign '${campaign.Title}' discarded: No free gift products configured.`);
        this._discardedCampaigns.add(campaign.Title);
        return;
    }

    const hasGift = cart.items.some(i =>
      campaign.Product_to_Offers.Product_to_Offer_FreeGift.some(g => g.freegif === i.id)
    );

    if (!hasGift) {
      campaign.Show_Popup_o_Automatic === 'Popup'
        ? this._showGiftPopup(campaign)
        : this._addGiftToCart(campaign);
    }
  }

  _showGiftPopup(campaign) {
    if (!campaign.Product_to_Offers.Product_to_Offer_FreeGift || campaign.Product_to_Offers.Product_to_Offer_FreeGift.length === 0) {
        console.warn(`Campaign '${campaign.Title}' discarded: No free gift items to display in popup.`);
        this._discardedCampaigns.add(campaign.Title);
        return;
    }    

    if (!this._currentPopup) {
      this._currentPopup = new GiftPopup(campaign, {
        onAdd: () => this._addGiftToCart(campaign),
        onClose: () => {
          this._discardedCampaigns.add(campaign.Title); 
          this._currentPopup = null;
        }
      });
    }
  }

  async _addGiftToCart(campaign) {
    const gift = campaign.Product_to_Offers.Product_to_Offer_FreeGift[0];
    const cart = document.querySelector("cart-notification") || document.querySelector("cart-drawer");

    if (gift == undefined) {
      this._discardedCampaigns.add(campaign.Title);      
      throw new Error('Gift could not be added.');
    }

    try {
      const sections = window.location.pathname !== '/cart'
        ? cart?.getSectionsToRender().map(section => section.id)
        : getSectionsToRender().map(section => section.section);
      
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: new URLSearchParams({
          id: gift.freegif,
          quantity: campaign.Product_to_Offers.free_quantity,
          'properties[_free_gift]': campaign.Title,
          sections: sections.join(',')
        })
      });


      if (!response.ok) {
        const errorData = await response.json();
        console.error('Failed to add gift:', errorData.message || 'Unknown error');
        this._discardedCampaigns.add(campaign.Title);
        throw new Error(errorData.message || 'Gift could not be added.');
      }

      const data = await response.json();
      
      if (window.location.pathname !== '/cart') {
        cart?.renderContents(data);
      } else {
        window.location.reload();
      }
    } catch (error) {
      console.error('Error adding gift (caught):', error);
    }
  }

  async _removeFromCart(key) {
    const cart = document.querySelector("cart-notification") || document.querySelector("cart-drawer");
    try {
      const sections = window.location.pathname !== '/cart'
        ? cart?.getSectionsToRender().map(section => section.id)
        : getSectionsToRender().map(section => section.section);
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: new URLSearchParams({
          id: key,
          quantity: 0,
          sections: sections.join(',')
        })
      });
      await response.json();
      window.location.reload();
    } catch (error) {
      console.error('Error removing gift:', error);
    }
  }
}

class GiftPopup {
  constructor(campaign, { onAdd, onClose }) {
    this._campaign = campaign;
    this._onAdd = onAdd;
    this._onClose = onClose;
    this._currentIndex = 0;
    this._popupElement = null;
    this._items = null;
    this._totalItems = 0;
    this._init();
  }

  _init() {
    this._createPopup();
    this._setupCarousel();
    this._addEventListeners();
  }

  _createPopup() {
    this._popupElement = document.createElement('div');
    this._popupElement.className = 'free-gift-popup';
    this._popupElement.insertAdjacentHTML('beforeend', this._popupTemplate());
    document.body.appendChild(this._popupElement);
    
    queueMicrotask(() => {
      this._popupElement.classList.add('active');
      this._popupElement.querySelector('.popup-content')?.classList.add('active');
    });
  }

  _popupTemplate() {
    return `
      <div class="popup-content">
        <button class="close-popup" aria-label="Close">×</button>
        <h2 class="title_free_gift">${this._campaign.Modal_Title}</h2>
        <p class="description_free_gift">${this._campaign.description_modal}</p>
        <div class="carousel-container">
          ${this._campaign.Product_to_Offers.Product_to_Offer_FreeGift
            .map((gift, i) => this._giftTemplate(gift, i))
            .join('')}
          <div class="carousel-controls">
            <button class="carousel-button carousel-prev" aria-label="Previous">❮</button>
            <button class="carousel-button carousel-next" aria-label="Next">❯</button>
          </div>
        </div>
      </div>
    `;
  }

  _giftTemplate(gift, index) {
    return `
      <div class="carousel-item ${index === 0 ? 'active' : ''}" data-index="${index}">
        <form method="post" action="/cart/add" class="gift-form" data-product-form>
          <input type="hidden" name="id" value="${gift.freegif}">
          <input type="hidden" name="quantity" value="${this._campaign.Product_to_Offers.free_quantity}">
          <input type="hidden" name="properties[_free_gift]" value="${this._campaign.Title}">
          <img src="${gift.freegifimage}" alt="${gift.freegiftitle}" loading="lazy">
          <h3>${gift.freegiftitle}</h3>
          <button type="submit" class="gift-option" aria-label="Add ${gift.freegiftitle} to cart">
            ADD TO CART
            <div class="loading-overlay">
              <svg class="spinner" viewBox="0 0 66 66">
                <circle class="path" fill="none" cx="33" cy="33" r="30"></circle>
              </svg>
            </div>
          </button>
        </form>
      </div>
    `;
  }

  _setupCarousel() {
    this._items = this._popupElement.querySelectorAll('.carousel-item');
    this._totalItems = this._items.length;
    this._updateButtons();
  }

  _addEventListeners() {
    const prev = this._popupElement.querySelector('.carousel-prev');
    const next = this._popupElement.querySelector('.carousel-next');
    const close = this._popupElement.querySelector('.close-popup');
    
    prev.addEventListener('click', () => this._prev());
    next.addEventListener('click', () => this._next());
    close.addEventListener('click', () => this._close());
    
    this._popupElement.querySelectorAll('.gift-form').forEach(form =>
      form.addEventListener('submit', e => this._handleSubmit(e))
    );
    
    document.addEventListener('keydown', e => this._handleKeyPress(e));
  }

  async _handleSubmit(e) {
    e.preventDefault();
    const form = e.target;
    const button = form.querySelector('.gift-option');
    const loader = form.querySelector('.loading-overlay');
    const cart = document.querySelector("cart-notification") || document.querySelector("cart-drawer");

    try {
      button.disabled = true;
      loader.classList.add('visible');
      
      const formData = new FormData(form);
      formData.append(
        'sections',
        window.location.pathname !== '/cart'
          ? cart?.getSectionsToRender().map(section => section.id).join(',')
          : getSectionsToRender().map(section => section.section).join(',')
      );
      formData.append('sections_url', window.location.pathname);

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to add gift due to stock or configuration issue.');
      }

      const data = await response.json();

      if (window.location.pathname !== '/cart') {
        cart?.renderContents(data);
      } else {
        getSectionsToRender().forEach(section => {
          const elementToReplace = document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
          elementToReplace.innerHTML = getSectionInnerHTML(data.sections[section.section], section.selector);
        });
      }
      this._close();
    } catch (error) {
      console.error('Error adding gift (Popup):', error);
      alert('Error al agregar el regalo: ' + error.message + '. La campaña será descartada si el error persiste.');
      this._close(); 
    } finally {
      button.disabled = false;
      loader.classList.remove('visible');
    }
  }

  _handleKeyPress(event) {
    switch (event.key) {
      case 'Escape': this._close(); break;
      case 'ArrowLeft': this._prev(); break;
      case 'ArrowRight': this._next(); break;
    }
  }

  _prev() {
    if (this._currentIndex > 0) {
      this._currentIndex--;
      this._updateCarousel();
    }
  }

  _next() {
    if (this._currentIndex < this._totalItems - 1) {
      this._currentIndex++;
      this._updateCarousel();
    }
  }

  _updateCarousel() {
    this._items.forEach((item, i) =>
      item.classList.toggle('active', i === this._currentIndex)
    );
    this._updateButtons();
  }

  _updateButtons() {
    const prev = this._popupElement.querySelector('.carousel-prev');
    const next = this._popupElement.querySelector('.carousel-next');
    prev.style.visibility = this._currentIndex === 0 ? 'hidden' : 'visible';
    next.style.visibility = this._currentIndex === this._totalItems - 1 ? 'hidden' : 'visible';
  }

  _close() {
    this._popupElement.classList.remove('active');
    this._popupElement.querySelector('.popup-content')?.classList.remove('active');
    
    setTimeout(() => {
      this._popupElement.remove();
      this._onClose();
    }, 300);
    
    document.removeEventListener('keydown', this._handleKeyPress);
  }
}

const documentReady = () => new Promise(resolve => {
  if (document.readyState === 'complete' || document.readyState === 'interactive') {
    resolve();
  } else {
    document.addEventListener('DOMContentLoaded', resolve, { once: true });
  }
});

const initializeGiftManager = async () => {
  try {
    const response = await fetch('/pages/searchgift', { credentials: 'same-origin' });
    const data = await response.json();
    const manager = new FreeGiftManager(data);
    if (window.location.pathname === '/cart') {
      await manager.checkCart();
    }
    return manager;
  } catch (error) {
    console.error('Initialization error:', error);
  }
};


function getSectionsToRender() {
  return [
    {
      id: 'main-cart-items',
      section: document.getElementById('main-cart-items')?.dataset.id,
      selector: '.js-contents'
    },
    {
      id: 'cart-icon-bubble',
      section: 'cart-icon-bubble',
      selector: '.shopify-section'
    },
    {
      id: 'cart-live-region-text',
      section: 'cart-live-region-text',
      selector: '.shopify-section'
    },
    {
      id: 'main-cart-footer',
      section: document.getElementById('main-cart-footer')?.dataset.id,
      selector: '.js-contents'
    }
  ].filter(section => section.section); 
}

function getSectionInnerHTML(html, selector) {
  return new DOMParser().parseFromString(html, 'text/html').querySelector(selector)?.innerHTML ?? '';
}

initializeGiftManager();